import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  Input,
  Select,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import DataTable, { Column } from '@/components/common/DataTable';
import apiClient from '@/services/letsparkyClient';
import { useAuthStore } from '@/stores/authStore';

type InvoiceSortBy = 'issued_at' | 'paid_at' | 'amount';
type SortDirection = 'asc' | 'desc';
type InvoiceStatus = 'UNPAID' | 'PAID' | 'VOID';

interface InvoiceSummaryUser {
  name?: string | null;
  surname?: string | null;
  email?: string | null;
  phone_number?: string | null;
}

interface InvoiceRecord {
  id: string;
  user_id: string;
  parking_id?: string | null;
  membership_id?: string | null;
  payment_id?: string | null;
  amount: string;
  currency: string;
  status: InvoiceStatus;
  issued_at: string;
  paid_at?: string | null;
  user?: InvoiceSummaryUser;
}

interface InvoiceFilters {
  user_id?: string;
  tenant_id?: string;
  status?: InvoiceStatus | InvoiceStatus[];
  from_date?: string;
  to_date?: string;
  sort_by?: InvoiceSortBy;
  sort_direction?: SortDirection;
  page?: number;
  limit?: number;
  search?: string;
}

const sortOptions: Array<{ value: InvoiceSortBy; labelKey: string }> = [
  { value: 'issued_at', labelKey: 'invoices.sort.issuedAt' },
  { value: 'paid_at', labelKey: 'invoices.sort.paidAt' },
  { value: 'amount', labelKey: 'invoices.sort.amount' },
];

/** API may return `{ success, data: { data, pagination } }` or a flat `{ data, pagination }`. */
function parseInvoiceListResponse(raw: unknown): { rows: InvoiceRecord[]; total: number } {
  if (!raw || typeof raw !== 'object') {
    return { rows: [], total: 0 };
  }

  const root = raw as Record<string, unknown>;

  // Envelope: { success?: boolean, data: { data: Invoice[], pagination } }
  const nested = root.data;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const payload = nested as Record<string, unknown>;
    if (Array.isArray(payload.data)) {
      const rows = payload.data as InvoiceRecord[];
      const pagination = payload.pagination as { total?: number } | undefined;
      return {
        rows,
        total: typeof pagination?.total === 'number' ? pagination.total : rows.length,
      };
    }
  }

  // Flat: { data: Invoice[], pagination }
  if (Array.isArray(root.data)) {
    const rows = root.data as InvoiceRecord[];
    const pagination = root.pagination as { total?: number } | undefined;
    return {
      rows,
      total: typeof pagination?.total === 'number' ? pagination.total : rows.length,
    };
  }

  return { rows: [], total: 0 };
}

const Invoices: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { user, token } = useAuthStore();
  const isSystem = user?.role === 'SYSTEM';

  const [invoices, setInvoices] = useState<InvoiceRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sortBy, setSortBy] = useState<InvoiceSortBy>('issued_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const statusOptions = useMemo(() => ['', 'UNPAID', 'PAID', 'VOID'], []);

  const buildParams = useCallback((): InvoiceFilters => {
    const params: InvoiceFilters = {
      page: currentPage,
      limit: pageSize,
      sort_by: sortBy,
      sort_direction: sortDirection,
    };

    if (!isSystem && user?.tenantId) {
      params.tenant_id = user.tenantId;
    }
    if (statusFilter) {
      params.status = statusFilter as InvoiceStatus;
    }
    if (fromDate) {
      params.from_date = fromDate;
    }
    if (toDate) {
      params.to_date = toDate;
    }
    if (searchQuery.trim()) {
      params.search = searchQuery.trim();
    }

    return params;
  }, [
    currentPage,
    isSystem,
    pageSize,
    searchQuery,
    sortBy,
    sortDirection,
    statusFilter,
    fromDate,
    toDate,
    user?.tenantId,
  ]);

  const fetchInvoices = useCallback(async () => {
    if (!token) return;

    setIsLoading(true);
    try {
      apiClient.client.setToken(token);
      const response = await apiClient.invoices.list(buildParams());
      const { rows, total } = parseInvoiceListResponse(response);
      setInvoices(rows);
      setTotalItems(total);
    } catch (error) {
      toast({
        title: t('invoices.fetchErrorTitle'),
        description: error instanceof Error ? error.message : t('common.unknown'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setInvoices([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [token, buildParams, toast, t]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const formatDate = (value?: string) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'PAID':
        return 'green';
      case 'UNPAID':
        return 'yellow';
      case 'VOID':
        return 'gray';
      default:
        return 'blue';
    }
  };

  const formatAmount = (amount: string, currency: string) => {
    const numericAmount = Number(amount);
    if (Number.isNaN(numericAmount)) return `${amount} ${currency}`.trim();

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      currencyDisplay: 'code',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericAmount);
  };

  const getUserDisplay = (invoice: InvoiceRecord) => {
    const fullName = [invoice.user?.name, invoice.user?.surname].filter(Boolean).join(' ').trim();
    const identity = fullName || invoice.user?.email || t('invoices.unknownUser');
    const subtitle = invoice.user?.email || invoice.user?.phone_number || invoice.user_id;

    return (
      <Box>
        <Text fontWeight="medium">{identity}</Text>
        {subtitle ? (
          <Text fontSize="sm" color="gray.500">
            {subtitle}
          </Text>
        ) : null}
      </Box>
    );
  };

  const columns: Column<InvoiceRecord>[] = [
    {
      header: t('invoices.columns.user'),
      accessor: (invoice) => getUserDisplay(invoice),
    },
    {
      header: t('invoices.columns.status'),
      accessor: (invoice) => (
        <Badge colorScheme={getStatusColor(invoice.status)}>
          {invoice.status || '-'}
        </Badge>
      ),
      sortable: true,
      sortKey: 'status',
    },
    {
      header: t('invoices.columns.amount'),
      accessor: (invoice) => formatAmount(invoice.amount, invoice.currency),
      isNumeric: true,
      sortable: true,
      sortKey: 'amount',
    },
    {
      header: t('invoices.columns.issuedAt'),
      accessor: (invoice) => formatDate(invoice.issued_at),
      sortable: true,
      sortKey: 'issued_at',
    },
    {
      header: t('invoices.columns.paidAt'),
      accessor: (invoice) => formatDate(invoice.paid_at ?? undefined),
      sortable: true,
      sortKey: 'paid_at',
    },
  ];

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleSort = (newSortBy: string, newSortDirection: SortDirection) => {
    if (!['issued_at', 'paid_at', 'amount'].includes(newSortBy)) return;
    setSortBy(newSortBy as InvoiceSortBy);
    setSortDirection(newSortDirection);
    setCurrentPage(1);
  };

  return (
    <Box>
      <Heading size="lg" mb={6}>
        {t('invoices.title')}
      </Heading>

      <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={4} mb={4}>
        <GridItem>
          <FormControl>
            <FormLabel>{t('invoices.filters.status')}</FormLabel>
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              {statusOptions.map((status) => (
                <option key={status || 'all'} value={status}>
                  {status ? t(`invoices.status.${status}`) : t('invoices.filters.allStatuses')}
                </option>
              ))}
            </Select>
          </FormControl>
        </GridItem>

        <GridItem>
          <FormControl>
            <FormLabel>{t('invoices.filters.fromDate')}</FormLabel>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setCurrentPage(1);
              }}
            />
          </FormControl>
        </GridItem>

        <GridItem>
          <FormControl>
            <FormLabel>{t('invoices.filters.toDate')}</FormLabel>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setCurrentPage(1);
              }}
            />
          </FormControl>
        </GridItem>

        <GridItem>
          <FormControl>
            <FormLabel>{t('invoices.filters.sortBy')}</FormLabel>
            <Select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value as InvoiceSortBy);
                setCurrentPage(1);
              }}
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </option>
              ))}
            </Select>
          </FormControl>
        </GridItem>
      </Grid>

      <Grid templateColumns={{ base: '1fr', md: '240px 1fr' }} gap={4} mb={4}>
        <GridItem>
          <FormControl>
            <FormLabel>{t('invoices.filters.direction')}</FormLabel>
            <Select
              value={sortDirection}
              onChange={(e) => {
                setSortDirection(e.target.value as SortDirection);
                setCurrentPage(1);
              }}
            >
              <option value="asc">{t('invoices.filters.asc')}</option>
              <option value="desc">{t('invoices.filters.desc')}</option>
            </Select>
          </FormControl>
        </GridItem>
      </Grid>

      <DataTable
        columns={columns}
        data={invoices}
        keyField="id"
        isLoading={isLoading}
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        sort_by={sortBy}
        sort_direction={sortDirection}
        onSearch={handleSearch}
        onSort={handleSort}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
      />
    </Box>
  );
};

export default Invoices;
